import { KeyValue } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { createMask } from '@ngneat/input-mask';
import { ComplianceSettingForm } from 'src/app/classes/form-groups/compliance-setting-form';
import { Company } from 'src/app/classes/models';
import { ComplianceSettingLiabilityType } from 'src/app/interfaces/compliance-setting-liability-types';
import { ToastService } from 'src/app/services/toast.service';
import { UtilityService } from 'src/app/services/utility.service';

@Component({
  selector: 'app-compliance-general-liability',
  templateUrl: './compliance-general-liability.component.html',
  styleUrls: ['./compliance-general-liability.component.less'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ComplianceGeneralLiabilityComponent implements OnInit, OnChanges {
  constructor(
    private UtilityService: UtilityService,
    private ToastService: ToastService
  ) {}

  @Input() complianceSettingForms?: ComplianceSettingForm[];
  @Input() company?: Company;
  @Input() amBestValueMap?: Map<string, string | number | null>;
  @Input() amBestFinancialValueMap?: Map<
    string,
    { value: string | null; hint: string }
  > | null;

  @Output() updateRequiredFields =
    new EventEmitter<ComplianceSettingLiabilityType>();

  collapseGeneralLiabilities: boolean = true;
  generalLiabilitiesMapArr: Map<
    string,
    {
      formControl: FormControl<any>;
      inputType: 'checkBox' | 'select' | 'number' | 'none';
      selectOptions?: Map<
        string,
        string | number | null | { value: string | null; hint: string }
      > | null;
      checkBoxText?: string;
      show: boolean;
    }
  >[] = [];

  currencyMask = createMask({
    alias: 'numeric',
    groupSeparator: ',',
    placeholder: '',
    rightAlign: false
  });

  applyCurrencyFormat(FormControl: FormControl, value: any) {
    FormControl.patchValue(
      value === null ? null : parseInt(value.replace(/[^\d]/g, ''), 10),
      { emitViewToModelChange: false }
    );
  }

  keyValuePairDefaultOrder(
    a: KeyValue<any, any>,
    b: KeyValue<any, any>
  ): number {
    return 0;
  }

  onGeneralLiabilityIsRequiredChange() {
    this.updateRequiredFields.emit('generalLiability');
  }

  getOptionValue(item: {
    key: string;
    value: string | number | { value: string | null; hint: string } | null;
  }) {
    if (item.value && typeof item.value === 'object') {
      return item.value.value;
    }
    return item.value;
  }

  buildGeneralLiabilitiesMapArrFromForms() {
    if (!this.complianceSettingForms) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing compliance setting forms.',
        { returnError: true }
      );
    }

    if (!this.company) {
      throw this.ToastService.addErrorToast('Error', 'Missing Company data.', {
        returnError: true
      });
    }

    const { country } = this.company;

    if (!country) {
      throw this.ToastService.addErrorToast(
        'Error',
        'Missing Company country.',
        { returnError: true }
      );
    }

    this.generalLiabilitiesMapArr = this.complianceSettingForms.map((form) => {
      const entries: [
        string,
        {
          formControl: FormControl<any>;
          inputType: 'checkBox' | 'select' | 'number' | 'none';
          selectOptions?: Map<
            string,
            string | number | null | { value: string | null; hint: string }
          > | null;
          checkBoxText?: string;
          show: boolean;
        }
      ][] = [
        [
          form.controls.riskLevel.value === 0
            ? 'Global'
            : `Risk Level ${form.controls.riskLevel.value}`,
          {
            formControl: form.controls.riskLevel,
            inputType: 'none',
            show: true
          }
        ],
        [
          'Policy Required?',
          {
            formControl: form.controls.generalLiability_isRequired,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'A.M. Best Rating',
          {
            formControl: form.controls.generalLiability_amBestRating,
            inputType: 'select',
            selectOptions: this.amBestValueMap,
            show: true
          }
        ],
        [
          'Waive Non-Rated A.M. Best Rating Carriers?',
          {
            formControl:
              form.controls.generalLiability_AllowNRRatedAMBestCarriers,
            inputType: 'checkBox',
            checkBoxText: 'Waived?',
            show: true
          }
        ],
        [
          'A.M. Best Financial Size',
          {
            formControl: form.controls.generalLiability_financialSizeCategory,
            inputType: 'select',
            selectOptions: this.amBestFinancialValueMap,
            show: true
          }
        ],
        [
          'Amount Per Occurrence',
          {
            formControl:
              form.controls.generalLiability_policyAmountPerOccurance,
            inputType: 'number',
            show: true
          }
        ],
        [
          'Amount - Aggregate',
          {
            formControl: form.controls.generalLiability_policyAmountAggregate,
            inputType: 'number',
            show: true
          }
        ],
        [
          'Prod-Comp/Op Aggregate',
          {
            formControl: form.controls.generalLiability_productOps,
            inputType: 'number',
            show: true
          }
        ],
        [
          'Medical Expenses',
          {
            formControl: form.controls.generalLiability_medicalExpenses,
            inputType: 'number',
            show: true
          }
        ],
        [
          'Personal Advertisement & Injury',
          {
            formControl: form.controls.personalAdvertisementInjury,
            inputType: 'number',
            show: true
          }
        ],
        [
          'Damage to Rented Premises',
          {
            formControl: form.controls.generalLiability_damageToRentedPremises,
            inputType: 'number',
            show: true
          }
        ]
        // ['HNO Auto Coverage', { formControl: form.controls.generalLiability_nonOwnedAutoAmount, inputType: 'number', show: true  }]
      ];

      if (country === 'CA') {
        entries.push(
          [
            'Hired Autos',
            {
              formControl: form.controls.generalLiability_hiredAutos,
              inputType: 'number',
              show: true
            }
          ],
          [
            'Non-Owned Autos',
            {
              formControl: form.controls.generalLiability_nonOwnedAutos,
              inputType: 'number',
              show: true
            }
          ]
        );
      }

      entries.push(
        [
          'Additional Insured',
          {
            formControl: form.controls.glIsNamed,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Additional Insured - Ongoing Operations',
          {
            formControl: form.controls.generalLiability_hasAiOngoingOps,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Additional Insured - Completed Operations',
          {
            formControl: form.controls.generalLiability_hasAiCompOps,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Primary and Non-Contributory (PNC)',
          {
            formControl: form.controls.generalLiability_hasPnc,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Waiver of Subrogation',
          {
            formControl: form.controls.glIsAgency,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Valid Description of Operations',
          {
            formControl: form.controls.glDescOps,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Contractual Liability',
          {
            formControl: form.controls.generalLiability_contractualLiability,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Pass Full Policy Review',
          {
            formControl: form.controls.generalLiability_hasFullPolicyDoc,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Has Required Forms',
          {
            formControl: form.controls.generalLiability_hasRequiredForms,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Occurrence based?',
          {
            formControl: form.controls.generalLiability_isPerOccurrence,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Per Proj/Loc Agg',
          {
            formControl: form.controls.generalLiability_perProjLocAgg,
            inputType: 'checkBox',
            show: true
          }
        ],
        [
          'Additional Insured Endt-Scheduled Names',
          {
            formControl: form.controls.glAdditionalInsuredEndorsements,
            inputType: 'checkBox',
            show: this.company?.allowAdditionalInsuredEndorsements || false
          }
        ],
        [
          'COI Name/Legal Name Mismatch',
          {
            formControl: form.controls.generalLiability_legalNameMismatch,
            inputType: 'checkBox',
            show: true
          }
        ]
      );

      return new Map(entries);
    });

    this.updateRequiredFields.emit('generalLiability');
  }

  ngOnInit(): void {
    this.buildGeneralLiabilitiesMapArrFromForms();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['complianceSettingForms'] &&
      !changes['complianceSettingForms'].firstChange
    ) {
      this.buildGeneralLiabilitiesMapArrFromForms();
    }
  }
}
